import { Button, Form } from 'antd';
import { useState } from 'react';

import { Drawer } from '@/components/drawer';
import { type FormBuilderOptionItem, GenericForm, type GenericFormProps } from '@/components/generic-form';
import { Loader } from '@/components/loader';
import type { VoidFnType } from '@/types/common';

export interface DrawerFormProps extends GenericFormProps {
  loaderCondition: boolean;
  title: string;
  initValues: any;
  formFields: FormBuilderOptionItem[];
  tailLayout?: object;
  onClose: VoidFnType;
  onSuccessFinish?: VoidFnType;
  onFinish?: (values: any) => void;
  visible?: boolean;
  useDisableSubmit?: boolean;
  /** Drawer width (default in `Drawer` is 500). */
  width?: number | string;
}

export const DrawerForm = ({
  title,
  initValues,
  formFields,
  onClose,
  onFinish,
  onSuccessFinish,
  loaderCondition,
  visible = true,
  useDisableSubmit = true,
  width,
  ...props
}: DrawerFormProps) => {
  const [formRef] = Form.useForm();
  const form = props.form || formRef;
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  // Start false: with pre-filled initialValues, onFieldsChange may not run until the user
  // edits a field — then Submit stayed disabled incorrectly (e.g. campaigns create).
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  const onStartSubmit = () => {
    setLoadingSubmit(true);
  };

  const onFinishSubmit = () => {
    setLoadingSubmit(false);
    setDisableSubmit(true);
  };

  const onFieldsChange = (_, all: any[]) => {
    const disable = all.some(({ errors }: any) => Boolean(errors.length));
    if (disableSubmit !== disable) {
      setDisableSubmit(disable);
    }
  };

  const DrawerFooter = ({ onClose }: { onClose: VoidFunction }) => {
    const disabled = useDisableSubmit ? disableSubmit : false;
    return (
      <div className="drawer-footer">
        <Button className="drawer-footer-button" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={form.submit} type="primary" loading={loadingSubmit} disabled={disabled}>
          Submit
        </Button>
      </div>
    );
  };

  const Footer = (onClose: VoidFnType) => <DrawerFooter onClose={onClose} />;

  return (
    <Drawer onClose={onClose} visible={visible} title={title} footer={Footer} width={width}>
      {loaderCondition ? (
        <Loader />
      ) : (
        <GenericForm
          {...props}
          formRef={form}
          initialValues={initValues}
          formFields={formFields}
          onFinish={onFinish}
          onSuccessFinish={onSuccessFinish || onClose}
          onStartSubmit={onStartSubmit}
          onFinishSubmit={onFinishSubmit}
          onFieldsChange={onFieldsChange}
        />
      )}
    </Drawer>
  );
};
